/**
 * The browser tab says where the user stands (I-3777), so several open tabs can
 * be told apart. The shell does it for every app: a route's breadcrumbs and the
 * app's name need nothing from the app, and a view with an open document or
 * record names it through `useConnectTileTitle`.
 *
 * With tabs and tiles the routes run in the frames, whose own title nobody sees:
 * a frame reports where it stands, and the page around it shows the active tile.
 */
import { computed, watch, watchEffect, type ComputedRef } from 'vue';
import type { useI18n } from 'vue-i18n';
import { START_LOCATION, type RouteLocationNormalizedLoaded, type Router } from 'vue-router';
import {
  connectPageRecord,
  connectTileId,
  reportConnectTile,
  useAccountConfig,
} from '@blpde/connect';
import { selectBreadcrumbTrail } from '@blpde/connect/config';
import i18n from '@/i18n';
import { appI18n } from '@/i18n/appI18n';
import { appForPath } from '@/integration';
import { TILE_CLOSING_PATH } from '@/tiles/frame';
import { activeTileName, locationName, routeLabel } from '@/tiles/tileLabels';
import { useTileWorkspaces } from '@/tiles/useTileWorkspaces';

type Translate = ReturnType<typeof useI18n>['t'];
type Crumb = (route: RouteLocationNormalizedLoaded, t: Translate) => string;

/** The path of the page around the tiles. */
const TILES_PATH = '/tiles';

/** `t` of a message table, for code outside a component. */
const tableT = (table: { global: { t: unknown } }) => table.global.t as Translate;

/** What of a record shows; `number` is the default. */
type BrowserTitleRecord = 'number' | 'name' | 'both';

interface BrowserTitleSettings {
  record?: BrowserTitleRecord;
  /** Whether the area or app stands beside a name. */
  area: boolean;
  /** Whether the product comes last. */
  product: boolean;
}

/** `shell.browser_title`: its declarations and defaults are in the settings hub. */
function useBrowserTitleSettings(): ComputedRef<BrowserTitleSettings> {
  const config = useAccountConfig<{
    browser_title?: { record?: BrowserTitleRecord; area?: boolean; product?: boolean };
  }>('shell');
  return computed(() => {
    const stored = config.effective.value?.browser_title;
    return {
      record: stored?.record,
      area: stored?.area !== false,
      product: stored?.product !== false,
    };
  });
}

/** What of a record shows; the other value stands in for a missing one. */
export function recordTitle(
  { number, name }: { number: string; name: string },
  show: BrowserTitleRecord | undefined,
): string {
  if (show === 'name') return name || number;
  if (show === 'both' && number && name && number !== name) return `${number} ${name}`;
  return number || name;
}

/**
 * The breadcrumb texts down to this route, its own last: the same trail as the
 * ERP's page header, whose crumbs only count on named routes.
 */
export function routeCrumbs(
  route: RouteLocationNormalizedLoaded,
  router: Pick<Router, 'getRoutes'>,
  t: Translate,
): string[] {
  const pattern = route.matched.at(-1)?.path;
  if (!pattern) return [];
  const crumbRoutes = router
    .getRoutes()
    .filter((record) => record.meta.breadcrumb && typeof record.name === 'string');
  return selectBreadcrumbTrail(pattern, crumbRoutes).map((record) =>
    (record.meta.breadcrumb as Crumb)(route, t),
  );
}

/** The route's own crumb names one record, not an area: `/sales/quotes/:quoteId`. */
export function showsOneRecord(route: Pick<RouteLocationNormalizedLoaded, 'matched'>): boolean {
  return route.matched.at(-1)?.path.split('/').at(-1)?.startsWith(':') ?? false;
}

/**
 * The area around a page. A record's own crumb only repeats what its name says
 * („Angebot #42“), so beside a name the crumb above it tells the area („Angebote“).
 * On a list the own crumb is the area, also beside a record embedded there.
 */
export function pageArea(title: string, crumbs: readonly string[], oneRecord: boolean): string {
  return crumbs.at(title && oneRecord ? -2 : -1) ?? '';
}

/** A browser cuts off the end of a tab's title, so the product comes last. */
export function browserTitle(name: string, product: string, withProduct = true): string {
  if (!name) return product;
  return withProduct ? `${name} – ${product}` : name;
}

export function installPageTitle(router: Router): void {
  const route = router.currentRoute;
  const settings = useBrowserTitleSettings();
  const title = computed(() => recordTitle(connectPageRecord(), settings.value.record));
  const area = computed(() => {
    const app = appForPath(route.value.path);
    // Crumbs are the app's texts: they read its own table (I-2284).
    const crumbs = app ? routeCrumbs(route.value, router, tableT(appI18n(app.id))) : [];
    return pageArea(title.value, crumbs, showsOneRecord(route.value));
  });

  // A browser tab opened from inside a tile keeps its `?tile=`, but nobody is around it.
  if (connectTileId() && window.parent !== window) {
    // Before its first page a frame knows nothing, and the closing route is no page:
    // what is stored stands (I-3806).
    const atPage = () => route.value !== START_LOCATION && route.value.path !== TILE_CLOSING_PATH;
    watch(
      [title, area, atPage],
      ([name, where, reporting]) => {
        if (reporting) reportConnectTile({ title: name, area: where });
      },
      // After the view has changed: a view naming itself in `setup` reports once.
      { immediate: true, flush: 'post' },
    );
    return;
  }

  // The name `index.html` gives the product stays its one source.
  const product = document.title;
  const tiles = useTileWorkspaces();
  const t = tableT(i18n);
  watchEffect(() => {
    const { path } = route.value;
    const { area: withArea, product: withProduct } = settings.value;
    let name = '';
    if (path === TILES_PATH) name = activeTileName(tiles.active.value, t, withArea);
    // Before the first navigation there is no page yet.
    else if (route.value !== START_LOCATION)
      name = locationName(
        { title: title.value, area: area.value, app: routeLabel(path, t) },
        withArea,
      );
    document.title = browserTitle(name, product, withProduct);
  });
}
